import { useEffect, useState } from 'preact/hooks';
import { type Session, describeError } from '../api/client';
import {
  addBook,
  finishBook,
  rateBook,
  readingLog,
  readingStatus,
  removeBook,
  resumeBook,
} from '../api/collections';
import { displayDate } from '../api/adapter';
import type { WireReadingLogItem } from '../api/wire';

import { EmptyState } from '../components/feedback/EmptyState.jsx';
import { Pressable } from '../components/a11y/Pressable';
import { SegmentedNav } from '../components/a11y/SegmentedNav';
import { ActionSheet } from '../components/sheets/ActionSheet.jsx';
import { SheetAction } from '../components/sheets/SheetAction.jsx';

/** Derived, not stored: a book is reading until it has an end date.
 *
 *  The design's third status, `ABANDONED` with a `stopped p.140`, has no schema
 *  behind it — `reading_log_items` carries `start_date`, `end_date` and `rating`
 *  and nothing else. Treated as a design-system invention rather than a gap to
 *  apologise for on screen. */
type Filter = 'ALL' | 'READING' | 'FINISHED';

/** The list pattern. Rating is the only inline edit; anything more opens a form.
 *
 *  A rating of 0 reads as the word `unrated` rather than zero stars, because
 *  zero stars looks like a judgement and an absence is not one.
 */
export function ReadingLog({
  session,
  journalDate,
}: {
  session: Session;
  /** Today, from the server. Dating a book uses the journal's day, not the
   *  device's. */
  journalDate: string;
}) {
  const [items, setItems] = useState<WireReadingLogItem[] | null>(null);
  const [filter, setFilter] = useState<Filter>('ALL');
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [acting, setActing] = useState<WireReadingLogItem | null>(null);
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');

  async function load() {
    try {
      setItems(await readingLog(session));
      setError(null);
    } catch (caught) {
      setError(describeError(caught));
    }
  }

  useEffect(() => {
    void load();
  }, [session]);

  async function toggleFinished(item: WireReadingLogItem) {
    try {
      const result =
        readingStatus(item) === 'finished'
          ? await resumeBook(session, item.id)
          : await finishBook(session, item.id, journalDate);
      if (result === 'conflict') setError('That book changed elsewhere. Reloaded.');
      await load();
    } catch (caught) {
      setError(describeError(caught));
    }
  }

  async function discard(item: WireReadingLogItem) {
    try {
      await removeBook(session, item.id);
      await load();
    } catch (caught) {
      setError(describeError(caught));
    }
  }

  async function rate(item: WireReadingLogItem, rating: number) {
    try {
      const result = await rateBook(session, item.id, rating);
      if (result === 'conflict') {
        setError('That book changed elsewhere. Reloaded — try again.');
      }
      await load();
    } catch (caught) {
      setError(describeError(caught));
    }
  }

  const reading = (items ?? []).filter((i) => readingStatus(i) === 'reading');
  const finished = (items ?? []).filter((i) => readingStatus(i) === 'finished');
  const groups =
    filter === 'READING'
      ? [{ label: 'READING', rows: reading }]
      : filter === 'FINISHED'
        ? [{ label: 'FINISHED', rows: finished }]
        : [
            { label: 'READING', rows: reading },
            { label: 'FINISHED', rows: finished },
          ];

  return (
    <main style={{ padding: 'var(--bujo-gutter)', maxWidth: 560, margin: '0 auto' }}>
      <header style={header}>
        <div style={eyebrow}>BUJO / COLLECTIONS</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h1 style={h1}>READING LOG</h1>
          <span style={countStyle}>
            {items === null ? '' : `${items.length} ${items.length === 1 ? 'TITLE' : 'TITLES'}`}
          </span>
        </div>
      </header>

      <SegmentedNav
        label="Reading status"
        active={filter}
        onSelect={setFilter}
        options={[
          { value: 'ALL', label: 'ALL', count: items?.length ?? 0 },
          { value: 'READING', label: 'READING', count: reading.length },
          { value: 'FINISHED', label: 'FINISHED', count: finished.length },
        ]}
      />

      {error ? <p style={errorLine}>! {error}</p> : null}

      <section style={{ paddingTop: 'var(--bujo-space-3)' }}>
        {items === null ? null : (items?.length ?? 0) === 0 ? (
          <EmptyState line="No books yet." detail="Add one above." />
        ) : (
          groups.map((group) =>
            group.rows.length === 0 ? null : (
              <div key={group.label}>
                <div style={groupLabel}>
                  {group.label} · {group.rows.length}
                </div>
                {group.rows.map((item) => (
                  <div key={item.id} style={row}>
                    {/* The title gets the whole width and truncates. Sharing the
                        line with the stars squeezed it into two and three
                        ragged lines — `The Art of the / Practice` — which is
                        what made this list read as messy. */}
                    <Pressable
                      label={`${item.title} — open actions`}
                      onPress={() => setActing(item)}
                      style={{ minHeight: 'var(--bujo-row-min)', display: 'flex', alignItems: 'center' }}
                    >
                      <span style={{ ...titleStyle, ...truncate }}>{item.title}</span>
                    </Pressable>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--bujo-space-3)' }}>
                      <span style={{ ...metaStyle, flex: 1, minWidth: 0, lineHeight: 1.4 }}>
                        {describe(item)}
                      </span>
                      <Stars value={item.rating} onRate={(n) => void rate(item, n)} />
                    </div>
                  </div>
                ))}
              </div>
            ),
          )
        )}

        {adding ? (
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              if (!title.trim()) return;
              try {
                await addBook(session, {
                  title: title.trim(),
                  author: author.trim() || undefined,
                  rating: 0,
                  start_date: journalDate,
                });
                setTitle('');
                setAuthor('');
                setAdding(false);
                await load();
              } catch (caught) {
                setError(
                  describeError(caught),
                );
              }
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bujo-space-2)', padding: 'var(--bujo-space-3) 0' }}
          >
            <input
              value={title}
              onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
              placeholder="title"
              autocapitalize="words"
              enterkeyhint="next"
              autofocus
              style={input}
            />
            <input
              value={author}
              onInput={(e) => setAuthor((e.target as HTMLInputElement).value)}
              placeholder="author (optional)"
              autocapitalize="words"
              enterkeyhint="done"
              style={input}
            />
            <div style={{ display: 'flex', gap: 'var(--bujo-space-3)' }}>
              <button type="submit" disabled={!title.trim()} style={{ ...action, flex: 1 }}>
                ADD
              </button>
              <button
                type="button"
                onClick={() => setAdding(false)}
                style={{ ...action, flex: 1, borderColor: 'var(--bujo-rule-hairline)', color: 'var(--bujo-text-meta)' }}
              >
                CANCEL
              </button>
            </div>
          </form>
        ) : (
          /* A quiet row at the foot of the list, as the design has it — a
             bordered slab at the top competed with the tabs and pushed the
             books themselves below the fold. */
          <button type="button" onClick={() => setAdding(true)} style={addRow}>
            + add a title
          </button>
        )}

      </section>

      {acting ? (
        <div style={sheetHost}>
          <ActionSheet
            label="BOOK"
            title={acting.title}
            meta={describe(acting)}
            onDismiss={() => setActing(null)}
            style={{
              maxHeight: '86vh',
              overflowY: 'auto',
              paddingBottom: 'env(safe-area-inset-bottom)',
              paddingLeft: 'calc(var(--bujo-gutter) + env(safe-area-inset-left))',
              paddingRight: 'calc(var(--bujo-gutter) + env(safe-area-inset-right))',
            }}
          >
            <Pressable
              label={
                readingStatus(acting) === 'finished'
                  ? 'Put it back on the reading list'
                  : 'Finish it today'
              }
              onPress={() => {
                const item = acting;
                setActing(null);
                void toggleFinished(item);
              }}
            >
              <SheetAction
                glyph={readingStatus(acting) === 'finished' ? '↺' : '✓'}
                label={readingStatus(acting) === 'finished' ? 'Reading again' : 'Finished'}
                hint={
                  readingStatus(acting) === 'finished'
                    ? 'clears the end date'
                    : `dates it ${short(journalDate)}`
                }
              />
            </Pressable>
            <Pressable
              label={`Remove ${acting.title}`}
              onPress={() => {
                const item = acting;
                setActing(null);
                void discard(item);
              }}
            >
              <SheetAction glyph="×" label="Remove" hint="deletes it" destructive />
            </Pressable>
          </ActionSheet>
        </div>
      ) : null}
    </main>
  );
}

/** Five glyphs, plus the fraction once there is one — `★★★★☆ 4/5`.
 *
 *  An unrated book shows five hollow glyphs and no text: `0/5` would assert a
 *  verdict where there is only an absence, and a word saying so is more
 *  furniture than the fact deserves. Colour is never the only signal — filled
 *  and hollow differ in shape, and the fraction states it outright when set. */
function Stars({ value, onRate }: { value: number; onRate: (n: number) => void }) {
  return (
    <span style={{ flex: 'none', display: 'flex', alignItems: 'baseline', gap: 'var(--bujo-space-2)' }}>
      <span style={{ display: 'flex' }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <Pressable
            key={n}
            label={`Rate ${n} of 5`}
            onPress={() => onRate(n === value ? 0 : n)}
            /* Was 15×14px — five of those in a row is a target nobody can hit
               with a thumb. Full 44px height; the width stays at 26 because five
               44px-wide stars would be 220px and crowd out the title on a
               320px screen. A 26×44 strip is the widest target this row can
               actually give each star. */
            style={{ width: 26, minHeight: 'var(--bujo-row-min)', flex: 'none' }}
          >
            <span
              style={{
                display: 'block',
                textAlign: 'center',
                font: 'var(--bujo-fw-regular) var(--bujo-fs-event)/1 var(--bujo-font-mono)',
                color: n <= value ? 'var(--bujo-accent)' : 'var(--bujo-text-faint)',
              }}
            >
              {n <= value ? '\u2605' : '\u2606'}
            </span>
          </Pressable>
        ))}
      </span>

    </span>
  );
}

/** `Kovach · nonfiction · 12 JUL → 28 JUL`, or `started 24 JUL` while reading.
 *  Author, genre and dates never truncate; only a title may. */
function describe(item: WireReadingLogItem): string {
  const parts = [item.author, item.genre].filter(Boolean);
  if (item.end_date) {
    parts.push(
      item.start_date
        ? `${short(item.start_date)} \u2192 ${short(item.end_date)}`
        : `finished ${short(item.end_date)}`,
    );
  } else if (item.start_date) {
    parts.push(`started ${short(item.start_date)}`);
  }
  return parts.join(' \u00b7 ');
}

function short(date: string): string {
  return displayDate(date).slice(0, 6);
}

const groupLabel = {
  padding: 'var(--bujo-space-4) 0 var(--bujo-space-1)',
  font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-label)',
  color: 'var(--bujo-label)',
};

const countStyle = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-label)',
  color: 'var(--bujo-text-meta)',
};

const sheetHost = {
  position: 'fixed' as const,
  inset: 0,
  zIndex: 35,
};

const addRow = {
  width: '100%',
  minHeight: 'var(--bujo-tap-min)',
  textAlign: 'left' as const,
  cursor: 'pointer',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-event)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-accent)',
  borderTop: '1px solid var(--bujo-rule-hairline)',
};

const header = {
  paddingBottom: 'var(--bujo-space-2)',
  marginBottom: 'var(--bujo-space-4)',
  borderBottom: '1px solid var(--bujo-rule-heavy)',
};
const eyebrow = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-eyebrow)',
  color: 'var(--bujo-text-meta)',
  paddingBottom: 'var(--bujo-space-2)',
};
const h1 = {
  margin: 0,
  font: 'var(--bujo-fw-medium) var(--bujo-fs-display)/1.1 var(--bujo-font-mono)',
  color: 'var(--bujo-accent)',
};
const row = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 2,
  minHeight: 'var(--bujo-row-min)',
  justifyContent: 'center' as const,
  padding: '6px 0',
  borderBottom: '1px solid var(--bujo-rule-hairline)',
};
const titleStyle = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-event)/var(--bujo-lh-row) var(--bujo-font-mono)',
  color: 'var(--bujo-text-primary)',
};
/** One line, then `…`. The brief makes truncation part of the language rather
 *  than an accident, and a book row is a scannable index entry — the full title
 *  is one tap away in the action sheet. */
const truncate = {
  display: 'block' as const,
  minWidth: 0,
  whiteSpace: 'nowrap' as const,
  overflow: 'hidden' as const,
  textOverflow: 'ellipsis' as const,
};
const metaStyle = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
};
const errorLine = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1.4 var(--bujo-font-mono)',
  color: 'var(--bujo-state-error)',
};
const input = {
  minHeight: 'var(--bujo-tap-min)',
  background: 'var(--bujo-surface-sheet)',
  border: '1px solid var(--bujo-rule-hairline)',
  color: 'var(--bujo-text-primary)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-sheet)/1 var(--bujo-font-mono)',
  padding: '0 var(--bujo-space-3)',
  borderRadius: 0,
};
const action = {
  minHeight: 'var(--bujo-tap-min)',
  border: '1px solid var(--bujo-accent)',
  color: 'var(--bujo-accent)',
  font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-label)',
  cursor: 'pointer',
};
